import { useState, useEffect } from 'react'

// Live MM:SS (or HH:MM:SS over an hour) countdown to a target timestamp.
// Self-contained ticker — drop it into any card without lifting state.
// Stops ticking once the deadline is in the past.
//
// ANU-91 — `isPaused` freezes the displayed value WITHOUT extending the
// DB deadline. The interval stops ticking; whatever `now` we have at the
// pause moment is what the user sees. When unpaused we snap `now` back
// to Date.now() and resume — so the timer "loses" the paused duration
// from the player's perspective (matches the safety-critical spec:
// "DB-level expires_at extension on pause is NOT in scope — visual
// freeze only").
export default function CountdownTimer({ expiresAt, prefix = 'T-', expiredLabel = 'EXPIRED', isPaused = false }) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!expiresAt || isPaused) return
    const target = new Date(expiresAt).getTime()
    setNow(Date.now())
    if (target <= Date.now()) return
    const id = setInterval(() => {
      const t = Date.now()
      setNow(t)
      if (t >= target) clearInterval(id)
    }, 1000)
    return () => clearInterval(id)
  }, [expiresAt, isPaused])

  if (!expiresAt) return null
  const msLeft = new Date(expiresAt).getTime() - now
  if (msLeft <= 0) return <span>{expiredLabel}</span>

  const total = Math.floor(msLeft / 1000)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const pad = (n) => String(n).padStart(2, '0')
  return <span>{prefix}{h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`}</span>
}
